import type { DrillGoal } from "@/domain/learning/model/drill-goal";
import type { Cursor } from "@/domain/vim/model/cursor";
import { Icon } from "@/presentation/components/icons/Icon";
import { LABELS } from "@/presentation/i18n/labels";
import "./TargetPanel.css";

interface TargetPanelProps {
  readonly goal: DrillGoal;
  readonly currentBuffer: readonly string[];
  readonly currentCursor: Cursor;
  readonly achieved: boolean;
}

/**
 * 的（目標パネル・ui-design.md §5.6）。読み取り専用。
 *
 * 「目標の完成形だけ」だと何を変えればよいか読み取れないため、紙面の稽古は
 * いまの行と目標の行を対照して見せる。移動だけの稽古は距離の言い切りに加え、
 * いま／目標の位置印を同じ行の上に置く。
 */
export function TargetPanel({ goal, currentBuffer, currentCursor, achieved }: TargetPanelProps) {
  const rootClass = ["target-panel", achieved ? "target-panel--achieved" : null]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={rootClass} aria-label={LABELS.goal}>
      {goal.buffer ? (
        <BufferTarget
          targetBuffer={goal.buffer}
          currentBuffer={currentBuffer}
          achieved={achieved}
        />
      ) : (
        <MovementTarget
          targetCursor={goal.cursor}
          currentCursor={currentCursor}
          currentBuffer={currentBuffer}
          achieved={achieved}
        />
      )}
    </section>
  );
}

interface BufferTargetProps {
  readonly targetBuffer: readonly string[];
  readonly currentBuffer: readonly string[];
  readonly achieved: boolean;
}

function BufferTarget({ targetBuffer, currentBuffer, achieved }: BufferTargetProps) {
  const caption = achieved ? "完成した" : "こうなれば合格";

  return (
    <div className="target-panel__prose">
      <p className="target-panel__caption">{caption}</p>
      <ul className="target-panel__list">
        {targetBuffer.map((line, index) => {
          const currentLine = currentBuffer[index] ?? "";
          const done = achieved || currentLine === line;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: 行内容が変わっても行番号自体は安定した識別子
            <li key={index} className="target-panel__item">
              <Icon
                name="target"
                decorative
                size={16}
                className={done ? "target-panel__item-icon--done" : "target-panel__item-icon"}
              />
              {done ? (
                <span className="target-panel__item-text">{line || " "}</span>
              ) : (
                <div className="target-panel__compare">
                  <div className="target-panel__now">
                    <span className="target-panel__compare-label">いま</span>
                    <span className="target-panel__item-text target-panel__item-text--now">
                      {currentLine || " "}
                    </span>
                  </div>
                  <div className="target-panel__want">
                    <span className="target-panel__compare-label">目標</span>
                    <span className="target-panel__item-text target-panel__item-text--want">
                      {line || " "}
                    </span>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface MovementTargetProps {
  readonly targetCursor: Cursor | undefined;
  readonly currentCursor: Cursor;
  readonly currentBuffer: readonly string[];
  readonly achieved: boolean;
}

function MovementTarget({
  targetCursor,
  currentCursor,
  currentBuffer,
  achieved,
}: MovementTargetProps) {
  const guideLineIndex = targetCursor?.line ?? currentCursor.line;
  const guideLine = currentBuffer[guideLineIndex] ?? "";

  return (
    <div className="target-panel__prose">
      <p className="target-panel__caption">目標</p>
      <div className="target-panel__item target-panel__item--movement">
        <Icon
          name="target"
          decorative
          size={20}
          className={achieved ? "target-panel__item-icon--done" : "target-panel__item-icon"}
        />
        <div className="target-panel__movement-block">
          <p className="target-panel__movement">
            {achieved ? "たどり着いた" : describeMovement(currentCursor, targetCursor)}
          </p>
          {!achieved && targetCursor && targetCursor.line === currentCursor.line && (
            <MovementGuide
              line={guideLine}
              currentCol={currentCursor.col}
              targetCol={targetCursor.col}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface MovementGuideProps {
  readonly line: string;
  readonly currentCol: number;
  readonly targetCol: number;
}

function MovementGuide({ line, currentCol, targetCol }: MovementGuideProps) {
  const width = Math.max(line.length, currentCol + 1, targetCol + 1, 1);
  const cells = Array.from({ length: width }, (_, col) => {
    const char = line[col] ?? " ";
    const isCurrent = currentCol === col;
    const isTarget = targetCol === col;
    return { char, isCurrent, isTarget, col };
  });

  return (
    <div className="target-panel__guide">
      <div className="target-panel__guide-line" aria-hidden="true">
        {cells.map((cell) => (
          <span
            key={cell.col}
            className={[
              "target-panel__guide-char",
              cell.isCurrent ? "target-panel__guide-char--current" : null,
              cell.isTarget ? "target-panel__guide-char--target" : null,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {cell.char}
          </span>
        ))}
      </div>
      <div className="target-panel__guide-marks" aria-hidden="true">
        {cells.map((cell) => (
          <span key={cell.col} className="target-panel__guide-mark-slot">
            {cell.isCurrent ? <span className="target-panel__mark-now" /> : null}
            {cell.isTarget ? <span className="target-panel__mark-goal" /> : null}
          </span>
        ))}
      </div>
      <p className="target-panel__guide-legend">
        <span className="target-panel__legend-item" role="img" aria-label="いまの位置">
          <span className="target-panel__mark-now" aria-hidden="true" />
          いま
        </span>
        <span className="target-panel__legend-item" role="img" aria-label="目標の位置">
          <span className="target-panel__mark-goal" aria-hidden="true" />
          目標
        </span>
      </p>
    </div>
  );
}

/** いまのカーソルから目標までの距離・向きを、日本語の一文に言い切る。 */
function describeMovement(current: Cursor, target: Cursor | undefined): string {
  if (!target) {
    return "この墨板の上を動いてみよう";
  }
  if (target.line !== current.line) {
    const lineDiff = target.line - current.line;
    const lineDir = lineDiff > 0 ? "下" : "上";
    return `あと${Math.abs(lineDiff)}行${lineDir}、${target.col + 1}文字目へ`;
  }
  const colDiff = target.col - current.col;
  if (colDiff === 0) {
    return "たどり着いた";
  }
  const colDir = colDiff > 0 ? "右" : "左";
  return `あと${Math.abs(colDiff)}文字${colDir}へ`;
}
